import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { AuthenticationHelper } from "../utils/AuthenticationHelper";
import { User } from "../models/User";
import { UserRegistrationData, userService } from "../services/UserService";

export interface AuthState {
    currentUser: Pick<User, "_id" | "username" | "name"> | null;
    isLoggedIn: boolean;
}

interface UserContextValue extends AuthState {
    login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
    register: (data: UserRegistrationData) => Promise<{ success: boolean; error?: string }>;
    logout: () => void;
}

const UserContext = createContext<UserContextValue>({
    currentUser: null,
    isLoggedIn: false,
    login: async () => ({ success: false }),
    register: async () => ({ success: false }),
    logout: () => undefined,
});

const authHelper = new AuthenticationHelper();

export const UserContextProvider = (props: { children: React.ReactNode }) => {
    const [state, setState] = useState<AuthState>(() => {
        const isLoggedIn = authHelper.isUserAuthenticated();
        if (!isLoggedIn) {
            return { currentUser: null, isLoggedIn: false };
        }
        const id = authHelper.getUserId();
        const profile = authHelper.getUserProfile();
        const currentUser =
            id && profile
                ? { _id: id, name: profile.name, username: profile.username }
                : null;
        return { currentUser, isLoggedIn };
    });

    useEffect(() => {
        if (!authHelper.isUserAuthenticated()) {
            setState({ currentUser: null, isLoggedIn: false });
        }
    }, []);

    const login = useCallback(
        async (username: string, password: string): Promise<{ success: boolean; error?: string }> => {
            const result = await userService.login(username, password);
            if (!result) {
                return { success: false, error: "Invalid username or password." };
            }
            authHelper.setUserSession(result.token, result.id, result.refreshToken);
            authHelper.setUserProfile({ name: username, username });
            setState({
                currentUser: { _id: result.id, username, name: username },
                isLoggedIn: true,
            });
            return { success: true };
        },
        [],
    );

    const register = useCallback(
        async (data: UserRegistrationData): Promise<{ success: boolean; error?: string }> => {
            const result = await userService.register(data);
            if (!result) {
                return { success: false, error: "Registration failed. The username may already be taken." };
            }
            authHelper.setUserSession(result.token, result.id, result.refreshToken);
            authHelper.setUserProfile({ name: data.name, username: data.username });
            setState({
                currentUser: { _id: result.id, username: data.username, name: data.name },
                isLoggedIn: true,
            });
            return { success: true };
        },
        [],
    );

    const logout = useCallback(() => {
        authHelper.clearUserSession();
        setState({ currentUser: null, isLoggedIn: false });
    }, []);

    return (
        <UserContext.Provider value={{ ...state, login, register, logout }}>
            {props.children}
        </UserContext.Provider>
    );
};

export const useUserContext = () => useContext(UserContext);

export const withUserContext = <P extends object>(Component: React.ComponentType<P>) => (props: P) => (
    <UserContextProvider>
        <Component {...props} />
    </UserContextProvider>
);
